import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  TrendingUp,
  MapPin,
  RefreshCw,
  Eye,
  DollarSign,
  Percent,
  Clock,
  AlertCircle
} from 'lucide-react';
import API_CONSTRUCTOR from '../../models/api-model';
import {
  formatCurrency,
  formatPercentage,
  formatDate,
  getStatusColor,
  getStatusLabel,
  getPropertyLocation
} from '../../utils/investorUtils';
import { COMMITMENT_STATUS } from '../../constants/investmentStatus';
import { toast } from 'sonner';

export const MyInvestments = () => {
  const navigate = useNavigate();
  const [commitments, setCommitments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  // Load commitments and stats
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const api = API_CONSTRUCTOR();

      // Load commitments
      const commitmentsResponse = await api.get('/commitments/my-commitments');
      setCommitments(commitmentsResponse.data || []);

      // Load stats
      const statsResponse = await api.get('/investments/statistics');
      setStats(statsResponse.data);
    } catch (err) {
      console.error('Error loading investments:', err);
      setError(err.response?.data?.error || 'Failed to load investments');
      toast.error('Failed to load your investments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filter commitments by status
  const filteredCommitments = commitments.filter(commitment => {
    if (statusFilter === 'all') return true;
    return commitment.commitment_status === statusFilter;
  });

  // View commitment detail
  const handleViewCommitment = (commitmentId) => {
    navigate(`/investments/${commitmentId}`);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">My Investments</h1>
        <p className="text-gray-600">
          View and manage all your investment commitments
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Total Invested</p>
                  <p className="text-2xl font-bold text-primary">{formatCurrency(stats.total_invested)}</p>
                </div>
                <DollarSign className="h-8 w-8 text-primary opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Expected Returns</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.expected_returns)}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Active Investments</p>
                  <p className="text-2xl font-bold">{stats.active_investments || 0}</p>
                </div>
                <Clock className="h-8 w-8 text-blue-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Completed</p>
                  <p className="text-2xl font-bold">{stats.completed_investments || 0}</p>
                </div>
                <Percent className="h-8 w-8 text-purple-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters and Actions */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Label className="text-sm font-medium">Filter by Status:</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Commitments</SelectItem>
                  <SelectItem value={COMMITMENT_STATUS.PENDING}>Pending Approval</SelectItem>
                  <SelectItem value={COMMITMENT_STATUS.APPROVED}>Approved</SelectItem>
                  <SelectItem value={COMMITMENT_STATUS.ACTIVE}>Active</SelectItem>
                  <SelectItem value={COMMITMENT_STATUS.COMPLETED}>Completed</SelectItem>
                  <SelectItem value={COMMITMENT_STATUS.REJECTED}>Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <Card className="mb-6 border-red-200 bg-red-50">
          <CardContent className="pt-6 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Commitments Table */}
      {filteredCommitments.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-center">
            <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">
              {statusFilter === 'all'
                ? 'You have no investment commitments yet.'
                : 'No commitments found with the selected status.'}
            </p>
            <Button className="mt-4" onClick={() => navigate('/opportunities')}>
              Browse Opportunities
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Your Commitments ({filteredCommitments.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Commitment</TableHead>
                    <TableHead>Investment Amount</TableHead>
                    <TableHead>Expected Return</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCommitments.map((commitment) => (
                    <TableRow key={commitment.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium">
                        <div className="max-w-[200px] truncate">
                          {commitment.property_address || 'Property'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          {commitment.property_city || 'N/A'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-blue-50 text-blue-700">
                          {formatPercentage(commitment.commitment_percentage, 0)}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-semibold">
                        {formatCurrency(commitment.commitment_amount)}
                      </TableCell>
                      <TableCell className="font-semibold text-green-600">
                        {formatCurrency(commitment.expected_total_return)}
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(commitment.commitment_status)}>
                          {getStatusLabel(commitment.commitment_status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(commitment.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewCommitment(commitment.id)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MyInvestments;
